import React, { useContext, useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import MessageBlockTreeNode from '../../../../../types/BotBuilder/MessageBlockTreeNode';
import MessageBlockContent from '../../../../../types/BotBuilder/MessageBlockContent';
import BotResponseTreeNode from '../../../../../types/BotBuilder/BotResponseTreeNode';
import { ResponseNodeData } from '../ResponseNode';
import PreviewModeContext from '../../../../../context/PreviewModeContext/PreviewModeContext';
import RichTextEditor from '../../../../../components/RichTextEditor/RichTextEditor';

interface ResponseBlockProps {
  index: number;
  id: string;
  step: MessageBlockContent;
  messageNode: MessageBlockTreeNode;
  data: ResponseNodeData;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  isEditing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  isLocked?: boolean;
}

function ResponseBlock({
  index,
  id,
  step,
  messageNode,
  data,
  onNodeUpdate,
  isEditing,
  onEditStart,
  onEditEnd,
  isLocked = false
}: Readonly<ResponseBlockProps>): React.ReactElement {
  const [tooltipMessage, setTooltipMessage] = useState(
    'Edit assistant response'
  );
  const blockToEdit =
    id === 'combined'
      ? {
          ...step,
          content: messageNode.messageBlocks
            .map((block) => block.content)
            .join('\n\n')
        }
      : messageNode.messageBlocks.find(
          (block: MessageBlockContent) => id === block.id
        );

  const { isPreviewMode, setIsPreviewMode } = useContext(PreviewModeContext);

  useEffect(() => {
    if (isPreviewMode && isEditing) {
      onEditEnd();
    }
  }, [isPreviewMode, isEditing]);

  const handleResponseStepClick = (e: React.MouseEvent): void => {
    if (isLocked) return;
    e.stopPropagation();
    onEditStart();
  };

  const onContentChange = (newContent: string): void => {
    if (id === 'combined') {
      const updatedBlocks = [
        {
          content: newContent || null,
          contentType: 'MARKDOWN' as const,
          blockOrder: 0,
          id:
            messageNode.messageBlocks.length > 0
              ? messageNode.messageBlocks[0].id
              : 'combined-content'
        }
      ];

      const updatedMessageNode = Object.create(
        Object.getPrototypeOf(messageNode)
      );
      Object.assign(updatedMessageNode, messageNode, {
        messageBlocks: updatedBlocks
      });

      const updatedNode = {
        ...data.node,
        content: updatedMessageNode
      };

      onNodeUpdate(updatedNode);
    } else if (messageNode.messageBlocks[index]) {
      const updatedBlocks = messageNode.messageBlocks.map(
        (block, blockIndex) =>
          blockIndex === index
            ? { ...block, content: newContent || null }
            : block
      );

      const updatedMessageNode = Object.create(
        Object.getPrototypeOf(messageNode)
      );
      Object.assign(updatedMessageNode, messageNode, {
        messageBlocks: updatedBlocks
      });

      const updatedNode = {
        ...data.node,
        content: updatedMessageNode
      };
      onNodeUpdate(updatedNode);
    }
  };

  const handlePreviewClick = (): void => {
    setIsPreviewMode(true);
  };

  if (!isPreviewMode && isEditing) {
    return (
      <RichTextEditor
        textContent={blockToEdit?.content}
        onChange={onContentChange}
        isEditable
        showToolbar
        placeholder='Enter response content'
        onPreview={handlePreviewClick}
      />
    );
  }

  const content = (
    <div
      className={`
        bg-white text-base rounded-lg max-w-full
        overflow-hidden break-words w-full border-none p-0 text-left
        ${isLocked ? 'cursor-default' : 'cursor-pointer'}
      `}
      onClick={isLocked ? undefined : handleResponseStepClick}
      onKeyDown={
        isLocked
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleResponseStepClick(e as any);
              }
            }
      }
      role={isLocked ? undefined : 'button'}
      tabIndex={isLocked ? undefined : 0}
    >
      {!step.content || step.content.trim() === '' ? (
        <div className='px-3 py-2 text-sm text-muted-foreground'>
          Enter response content
        </div>
      ) : (
        <RichTextEditor
          textContent={step.content}
          isEditable={false}
          showToolbar={false}
          className='preview-mode'
          placeholder='Enter response content'
        />
      )}
    </div>
  );

  if (isLocked) {
    return content;
  }

  return (
    <Tooltip
      title={tooltipMessage}
      arrow
      onMouseEnter={() => setTooltipMessage('Edit assistant response')}
    >
      {content}
    </Tooltip>
  );
}

export default ResponseBlock;
